/**
 * Statistiques (design system, EcranStatistiques ; spécification §9, contenu proposé) : un
 * paquet principal ou tous, sur 7 jours, 30 jours ou 1 an. Chiffres clés, prévisions des
 * 30 prochains jours, activité et taux de réussite des cartes acquises face à l'objectif de
 * mémorisation. Tout est lu dans la base à l'ouverture et au changement de filtre.
 */
import { useEffect, useMemo, useState } from 'react';
import { State } from '../../fsrs/planificateur';
import type { ReglagesPaquet } from '../../paquets/reglages';
import {
  activite,
  activiteParSemaine,
  additionner,
  chiffresCles,
  previsions,
  retention,
  type ActiviteDuJour,
  type PrevisionDuJour,
  type RetentionDeLaSemaine,
} from '../../seance/statistiques';
import type { CarteLue, Paquet, RevisionLue } from '../../supabase/conversions';
import { CommandeSegmentee } from '../composants/CommandeSegmentee';
import { EtatVide } from '../composants/EtatVide';
import { ListeDeroulante } from '../composants/ListeDeroulante';
import { Coquille } from '../Coquille';
import { useDonnees } from '../donnees/contexte';
import { messageErreur } from '../donnees/erreurs';
import { dureeCourte } from '../format';
import { ESPACE_FINE, ESPACE_INSECABLE as E, nombre, pluriel } from '../outils';
import { naviguer } from '../routes';
import {
  jourCourt,
  jourLong,
  jourMois,
  jourTableau,
  majuscule,
  pourcent,
} from '../statistiques/Graphique';
import { GraphiqueEnBarres, totalColonne } from '../statistiques/GraphiqueEnBarres';
import { GraphiqueRetention, tauxDeLaSemaine } from '../statistiques/GraphiqueRetention';

type Periode = '7' | '30' | '365';
const PERIODES: { valeur: Periode; libelle: string }[] = [
  { valeur: '7', libelle: `7${E}j` },
  { valeur: '30', libelle: `30${E}j` },
  { valeur: '365', libelle: `1${E}an` },
];
/** En dessous, l'écran dit « Pas encore assez de révisions » (choix à confirmer). */
const CARTES_REVISEES_MINIMUM = 10;
const JOURS_DE_PREVISIONS = 30;
const JOUR = 86_400_000;

interface DonneesDuPaquet {
  paquet: Paquet;
  reglages: ReglagesPaquet;
  cartes: CarteLue[];
  revisions: RevisionLue[];
}

interface Chargees {
  cle: string;
  maintenant: Date;
  paquets: DonneesDuPaquet[];
}

/** « de Fac », « d’Anatomie ». */
function deNom(nom: string): string {
  return /^[aeiouyàâäéèêëîïôöùûüœæ]/i.test(nom) ? `d’${nom}` : `de ${nom}`;
}

/** Variation en clair, vrai signe moins : « +12 % sur 30 j ». */
function texteVariation(v: number | null, periode: string): string | undefined {
  if (v === null) return undefined;
  const p = Math.round(v * 100);
  const signe = p > 0 ? '+' : p < 0 ? '−' : '';
  return `${signe}${Math.abs(p)}${ESPACE_FINE}% sur ${periode}`;
}

function ChiffreCle({
  libelle,
  valeur,
  unite,
  suite,
  variation,
}: {
  libelle: string;
  valeur: string;
  unite?: string;
  suite?: string;
  variation?: string | undefined;
}) {
  return (
    <div className="fc-chiffre-cle">
      <dt className="fc-chiffre-cle__libelle">{libelle}</dt>
      <dd className="fc-chiffre-cle__valeur">
        {valeur}
        {unite && (
          <>
            {ESPACE_FINE}
            <span className="fc-chiffre-cle__unite">{unite}</span>
          </>
        )}
        {suite && `${ESPACE_FINE}${suite}`}
      </dd>
      {variation && <dd className="fc-chiffre-cle__variation">{variation}</dd>}
    </div>
  );
}

export function EcranStatistiques({ paquetDemande }: { paquetDemande?: string }) {
  const { depot, paquets, jours } = useDonnees();
  const principaux = useMemo(() => (paquets ?? []).filter((p) => p.parentId === null), [paquets]);
  // Un sous-paquet demandé (lien depuis une séance) mène à son paquet principal.
  const principalDe = (id: string | undefined): string | null => {
    let paquet = paquets?.find((p) => p.id === id);
    while (paquet?.parentId) paquet = paquets?.find((p) => p.id === paquet!.parentId);
    return paquet?.id ?? null;
  };
  const choixInitial =
    principalDe(paquetDemande) ?? (principaux.length === 1 ? principaux[0]!.id : 'tous');
  const [choix, setChoix] = useState<string>(choixInitial);
  const [periode, setPeriode] = useState<Periode>('30');
  const [chargees, setChargees] = useState<Chargees | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const selection = useMemo(
    () => (choix === 'tous' ? principaux : principaux.filter((p) => p.id === choix)),
    [choix, principaux],
  );
  const joursDePeriode = Number(periode);
  // Le journal est lu sur deux périodes (variation) et au moins 13 semaines (taux de réussite).
  const profondeur = periode === '365' ? 2 * 365 + 7 : 13 * 7 + 7;
  const cle = `${choix}|${profondeur}|${selection.map((p) => p.id).join(',')}`;

  useEffect(() => {
    if (paquets === null) return;
    let actif = true;
    const maintenant = new Date();
    const depuis = new Date(maintenant.getTime() - profondeur * JOUR);
    Promise.all(
      selection.map(async (paquet): Promise<DonneesDuPaquet> => {
        const [reglages, cartes, revisions] = await Promise.all([
          depot.reglages(paquet.id),
          depot.cartesDuPaquet(paquet.id),
          depot.revisionsDuPaquet(paquet.id, depuis),
        ]);
        return { paquet, reglages, cartes, revisions };
      }),
    )
      .then((resultat) => {
        if (!actif) return;
        setChargees({ cle, maintenant, paquets: resultat });
        setErreur(null);
      })
      .catch((e: unknown) => actif && setErreur(messageErreur(e)));
    return () => {
      actif = false;
    };
    // `cle` résume le paquet choisi et la profondeur du journal à lire.
  }, [cle, depot]);

  const nomChoisi =
    choix === 'tous' ? 'Tous les paquets' : (principaux.find((p) => p.id === choix)?.nom ?? '');

  const calcul = useMemo(() => {
    if (!chargees) return null;
    const { maintenant } = chargees;
    const liste = chargees.paquets;
    const prevues: PrevisionDuJour[][] = liste.map(({ paquet, reglages, cartes }) => {
      const compteurs = jours.get(paquet.id)?.parPaquet.get(paquet.id);
      const serie = previsions(
        cartes,
        reglages,
        maintenant,
        compteurs?.nouvelles ?? reglages.nouvellesCartesParJour,
        JOURS_DE_PREVISIONS,
      );
      // Aujourd'hui : les compteurs du paquet (limites du jour appliquées).
      if (compteurs && serie[0]) {
        serie[0] = {
          ...serie[0],
          nouvelles: compteurs.nouvelles,
          apprentissage: compteurs.apprentissage,
          acquises: compteurs.aRevoir,
        };
      }
      return serie;
    });
    const activites: ActiviteDuJour[][] = liste.map(({ reglages, revisions }) =>
      periode === '365'
        ? activiteParSemaine(revisions, reglages.heureDebutJournee, maintenant, 52)
        : activite(revisions, reglages.heureDebutJournee, maintenant, joursDePeriode),
    );
    const nSemaines = periode === '365' ? 52 : 12;
    const retentions: RetentionDeLaSemaine[][] = liste.map(({ reglages, revisions }) =>
      retention(revisions, reglages.heureDebutJournee, maintenant, nSemaines),
    );
    // Objectif : celui du paquet ; pour tous les paquets, moyenne pondérée par les révisions
    // de cartes acquises (simple moyenne sans révision).
    const objectifs = liste.map((d) => d.reglages.objectifMemorisation * 100);
    const poids = retentions.map((s) => s.reduce((a, x) => a + x.revisions, 0));
    const poidsTotal = poids.reduce((a, b) => a + b, 0);
    const objectif =
      objectifs.length === 0
        ? 90
        : poidsTotal > 0
          ? objectifs.reduce((a, o, i) => a + o * poids[i]!, 0) / poidsTotal
          : objectifs.reduce((a, b) => a + b, 0) / objectifs.length;
    const objectifUnique = objectifs.every((o) => o === objectifs[0]);
    return {
      previsions: additionner(prevues),
      activite: additionner(activites),
      retention: additionner(retentions),
      objectif: Math.round(objectif),
      objectifUnique,
      chiffres: chiffresCles(
        liste.map(({ revisions, reglages }) => ({
          revisions,
          heureDebut: reglages.heureDebutJournee,
        })),
        maintenant,
        joursDePeriode,
      ),
      cartesRevisees: liste.reduce(
        (a, d) => a + d.cartes.filter((c) => c.fsrs.state !== State.New).length,
        0,
      ),
    };
  }, [chargees, jours, periode, joursDePeriode]);

  const chargement = chargees === null || chargees.cle !== cle;
  const libellePeriode = PERIODES.find((p) => p.valeur === periode)!.libelle;

  const choisir = (paquet: string, nouvellePeriode: Periode) => {
    setChoix(paquet);
    setPeriode(nouvellePeriode);
    naviguer(
      paquet === 'tous' ? { ecran: 'statistiques' } : { ecran: 'statistiques', paquet },
      true,
    );
  };

  const entete = (
    <div className="fc-fenetre__entete" style={{ marginBottom: 0 }}>
      <h2 className="fc-fenetre__titre-ecran">Statistiques</h2>
      <div className="fc-fenetre__actions">
        <div className="fc-ecran-statistiques__filtres">
          <ListeDeroulante
            etiquette="Paquet"
            options={[
              { valeur: 'tous', libelle: 'Tous les paquets', icone: 'folder' },
              ...principaux.map((p) => ({
                valeur: p.id,
                libelle: p.nom,
                icone: 'layers' as const,
              })),
            ]}
            valeur={choix}
            onChoisir={(v) => choisir(v, periode)}
          />
          <CommandeSegmentee
            etiquette="Période"
            options={PERIODES}
            valeur={periode}
            onChoisir={(v) => choisir(choix, v)}
          />
        </div>
      </div>
    </div>
  );

  let contenu;
  if (erreur) {
    contenu = (
      <div className="fc-ecran-statistiques__vide">
        <EtatVide
          icone="cloud-off"
          titre="Statistiques indisponibles"
          texte={`Les révisions n’ont pas pu être lues. ${erreur}`}
        />
      </div>
    );
  } else if (!calcul) {
    contenu = <Squelettes />;
  } else if (calcul.cartesRevisees < CARTES_REVISEES_MINIMUM) {
    const n = calcul.cartesRevisees;
    contenu = (
      <div className="fc-ecran-statistiques__vide">
        <EtatVide
          icone="chart-column"
          titre="Pas encore assez de révisions"
          texte={`Les statistiques${choix === 'tous' ? '' : ` ${deNom(nomChoisi)}`} apparaîtront après quelques séances. ${
            n === 0
              ? 'Aucune carte n’a été révisée pour l’instant.'
              : `${pluriel(n, 'carte')} ${n > 1 ? 'ont été révisées' : 'a été révisée'} pour l’instant.`
          }`}
        />
      </div>
    );
  } else {
    contenu = (
      <Statistiques
        calcul={calcul}
        nom={nomChoisi}
        periode={periode}
        libellePeriode={libellePeriode}
        chargement={chargement}
      />
    );
  }

  return (
    <Coquille titre="Statistiques" colonne="large">
      <div className="fc-ecran-statistiques" aria-busy={chargement || undefined}>
        {entete}
        {contenu}
      </div>
    </Coquille>
  );
}

type Calcul = {
  previsions: PrevisionDuJour[];
  activite: ActiviteDuJour[];
  retention: RetentionDeLaSemaine[];
  objectif: number;
  objectifUnique: boolean;
  chiffres: ReturnType<typeof chiffresCles>;
};

function Statistiques({
  calcul,
  nom,
  periode,
  libellePeriode,
  chargement,
}: {
  calcul: Calcul;
  nom: string;
  periode: Periode;
  libellePeriode: string;
  chargement: boolean;
}) {
  const { chiffres, objectif } = calcul;
  const minutes = Math.round(chiffres.tempsPasse / 60);
  const dues = calcul.previsions;
  const demain = dues[1] ? totalColonne(dues[1]) : 0;

  // Prévisions
  const totalPrevu = dues.reduce((a, d) => a + totalColonne(d), 0);
  const nomPrevision = (i: number) =>
    i === 0 ? 'Aujourd’hui' : i === 1 ? 'Demain' : majuscule(jourLong(dues[i]!.date));

  // Activité
  const parSemaine = periode === '365';
  const act = calcul.activite;
  const n = act.length;
  const cartesActivite = act.reduce((a, d) => a + totalColonne(d), 0);
  const minutesActivite = act.reduce((a, d) => a + d.secondes, 0) / 60;
  const actifs = act.filter((d) => totalColonne(d) > 0).length;
  const nomActivite = (i: number) =>
    parSemaine
      ? `${i === n - 1 ? 'Cette semaine' : `Semaine du ${jourMois(act[i]!.date)}`}`
      : i === n - 1
        ? 'Aujourd’hui'
        : i === n - 2
          ? 'Hier'
          : majuscule(jourLong(act[i]!.date));
  const periodeActivite = parSemaine ? 'les 52 dernières semaines' : `les ${n} derniers jours`;
  const pasActivite = parSemaine ? 4 : n <= 7 ? 1 : 7;

  // Taux de réussite
  const sem = calcul.retention;
  const ns = sem.length;
  const recentes = sem.slice(-4);
  const revRecentes = recentes.reduce((a, s) => a + s.revisions, 0);
  const oubliRecentes = recentes.reduce((a, s) => a + s.aRevoir, 0);
  const libelleObjectif = calcul.objectifUnique
    ? `Objectif de mémorisation ${pourcent(objectif)}`
    : `Objectif moyen ${pourcent(objectif)}`;
  const nomSemaine = (i: number) =>
    `Semaine du ${jourMois(sem[i]!.debut)}${i === ns - 1 ? ', en cours' : ''}`;
  const pasRetention = ns > 12 ? 4 : 2;

  return (
    <>
      <div className="fc-ecran-statistiques__chiffres">
        <dl className="fc-chiffre-cle-rangee">
          <ChiffreCle
            libelle="Cartes révisées"
            valeur={nombre(chiffres.cartesRevisees)}
            variation={texteVariation(chiffres.variationCartes, libellePeriode)}
          />
          {chiffres.tauxDeReussite === null ? (
            <ChiffreCle
              libelle="Taux de réussite"
              valeur="—"
              variation="Aucune carte acquise révisée"
            />
          ) : (
            <ChiffreCle
              libelle="Taux de réussite"
              valeur={String(Math.round(chiffres.tauxDeReussite * 100))}
              unite="%"
              variation={`Acquises · objectif ${pourcent(objectif)}`}
            />
          )}
          {minutes < 60 ? (
            <ChiffreCle
              libelle="Temps passé"
              valeur={String(minutes)}
              unite="min"
              variation={texteVariation(chiffres.variationTemps, libellePeriode)}
            />
          ) : (
            <ChiffreCle
              libelle="Temps passé"
              valeur={nombre(Math.floor(minutes / 60))}
              unite="h"
              {...(minutes % 60 ? { suite: String(minutes % 60).padStart(2, '0') } : {})}
              variation={texteVariation(chiffres.variationTemps, libellePeriode)}
            />
          )}
          <ChiffreCle libelle="Cartes dues demain" valeur={nombre(demain)} />
        </dl>
      </div>
      <div className="fc-ecran-statistiques__graphiques">
        <GraphiqueEnBarres
          classe="fc-graphique-previsions"
          titre="Prévisions"
          resume={`${nom}, ${pluriel(dues[0] ? totalColonne(dues[0]) : 0, 'carte')} aujourd’hui et ${nombre(totalPrevu)} sur les 30 prochains jours`}
          colonnes={dues}
          courante={0}
          nom={nomPrevision}
          titreInfobulle={(i) =>
            i <= 1 ? `${nomPrevision(i)}, ${jourLong(dues[i]!.date)}` : nomPrevision(i)
          }
          etiquette={(i) =>
            i === 0 ? 'Aujourd’hui' : i % 7 === 0 ? jourCourt(dues[i]!.date) : null
          }
          participe="due"
          nomDuGroupe={`Cartes dues par jour, du ${jourMois(dues[0]!.date)} au ${jourMois(dues.at(-1)!.date)}. Flèches gauche et droite pour changer de jour.`}
          vide={
            totalPrevu === 0
              ? {
                  titre: 'Aucune carte due dans les 30 prochains jours',
                  texte: 'Les cartes ajoutées au paquet apparaîtront ici, jour par jour.',
                }
              : null
          }
          chargement={chargement}
          tableau={{
            legende: `Cartes dues par jour, du ${jourMois(dues[0]!.date)} au ${jourMois(dues.at(-1)!.date)}`,
            entetes: ['Jour', 'Nouvelles', 'Apprentissage', 'Acquises', 'Total'],
            lignes: dues.map((d, i) => ({
              cle: d.date,
              courante: i === 0,
              cellules: [
                i === 0 ? 'Aujourd’hui' : jourTableau(d.date),
                nombre(d.nouvelles),
                nombre(d.apprentissage),
                nombre(d.acquises),
                nombre(totalColonne(d)),
              ],
            })),
          }}
        />
        <GraphiqueEnBarres
          classe="fc-graphique-activite"
          titre="Activité"
          resume={`${nom}, ${pluriel(cartesActivite, 'carte révisée', 'cartes révisées')} en ${dureeCourte(minutesActivite)}, ${actifs} ${parSemaine ? (actifs > 1 ? 'semaines' : 'semaine') : actifs > 1 ? 'jours' : 'jour'} de révision sur ${n}`}
          colonnes={act}
          courante={n - 1}
          nom={nomActivite}
          titreInfobulle={(i) =>
            !parSemaine && i >= n - 2
              ? `${nomActivite(i)}, ${jourLong(act[i]!.date)}`
              : nomActivite(i)
          }
          etiquette={(i) =>
            // Une date tous les `pasActivite` en remontant ; en semaines, « Cette semaine »,
            // plus long, laisse libre la place de la date précédente.
            (n - 1 - i) % pasActivite === 0 &&
            (!parSemaine || i === n - 1 || n - 1 - i > pasActivite)
              ? i === n - 1
                ? parSemaine
                  ? 'Cette semaine'
                  : 'Aujourd’hui'
                : jourCourt(act[i]!.date)
              : null
          }
          participe="révisée"
          nomDuGroupe={`Cartes révisées par ${parSemaine ? 'semaine' : 'jour'}, du ${jourMois(act[0]!.date)} au ${jourMois(act.at(-1)!.date)}. Flèches gauche et droite pour changer de ${parSemaine ? 'semaine' : 'jour'}.`}
          vide={
            cartesActivite === 0
              ? {
                  titre: `Aucune révision sur ${periodeActivite}`,
                  texte: `Les cartes révisées en séance apparaîtront ici, ${parSemaine ? 'semaine par semaine' : 'jour par jour'}.`,
                }
              : null
          }
          chargement={chargement}
          tableau={{
            legende: `Cartes révisées par ${parSemaine ? 'semaine' : 'jour'}, du ${jourMois(act[0]!.date)} au ${jourMois(act.at(-1)!.date)}`,
            entetes: [
              parSemaine ? 'Semaine du' : 'Jour',
              'Nouvelles',
              'Apprentissage',
              'Acquises',
              'Total',
              'Temps',
            ],
            lignes: [...act].reverse().map((d, k) => {
              const i = n - 1 - k;
              return {
                cle: d.date,
                courante: i === n - 1,
                cellules: [
                  i === n - 1
                    ? parSemaine
                      ? 'Cette semaine'
                      : 'Aujourd’hui'
                    : parSemaine
                      ? jourMois(d.date)
                      : jourTableau(d.date),
                  nombre(d.nouvelles),
                  nombre(d.apprentissage),
                  nombre(d.acquises),
                  nombre(totalColonne(d)),
                  dureeCourte(d.secondes / 60),
                ],
              };
            }),
          }}
        />
        <GraphiqueRetention
          resume={
            revRecentes > 0
              ? `${nom}, cartes acquises par semaine sur les ${ns} dernières semaines ; ${pourcent((100 * (revRecentes - oubliRecentes)) / revRecentes)} sur les 4 dernières, pour un objectif de ${pourcent(objectif)}. Point creux : moins de 5 révisions dans la semaine`
              : `${nom}, aucune carte acquise révisée ces 4 dernières semaines ; objectif ${pourcent(objectif)}`
          }
          semaines={sem}
          objectif={objectif}
          libelleObjectif={libelleObjectif}
          nomSemaine={nomSemaine}
          etiquette={(i) => ((ns - 1 - i) % pasRetention === 0 ? jourCourt(sem[i]!.debut) : null)}
          nomDuGroupe={`Taux de réussite des cartes acquises par semaine, du ${jourMois(sem[0]!.debut)} au ${jourMois(sem.at(-1)!.debut)} ; ${libelleObjectif.toLowerCase()}. Flèches gauche et droite pour changer de semaine.`}
          vide={
            sem.every((s) => s.revisions === 0)
              ? {
                  titre: 'Aucune carte acquise révisée pour l’instant',
                  texte: 'Le taux apparaît dès qu’une carte acquise revient en séance.',
                }
              : null
          }
          chargement={chargement}
          tableau={{
            legende: `Réponses autres que 1 (À revoir) sur les cartes acquises, par semaine, du ${jourMois(sem[0]!.debut)} au ${jourMois(sem.at(-1)!.debut)}`,
            entetes: ['Semaine du', 'Révisions', 'Réponses 1 (À revoir)', 'Taux de réussite'],
            lignes: [...sem].reverse().map((s, k) => {
              const i = ns - 1 - k;
              const t = tauxDeLaSemaine(s);
              return {
                cle: s.debut,
                courante: i === ns - 1,
                cellules: [
                  `${jourMois(s.debut)}${i === ns - 1 ? ' (en cours)' : ''}`,
                  nombre(s.revisions),
                  nombre(s.aRevoir),
                  t == null ? 'Sans révision' : pourcent(t),
                ],
                ...(t == null ? { sansValeur: [3] } : {}),
              };
            }),
          }}
        />
      </div>
    </>
  );
}

/** Chargement : squelettes des panneaux (après ≈ 300 ms, fc-chargement). */
function Squelettes() {
  return (
    <div className="fc-chargement" style={{ display: 'contents' }}>
      <div className="fc-ecran-statistiques__chiffres fc-chargement">
        <dl className="fc-chiffre-cle-rangee">
          {['Cartes révisées', 'Taux de réussite', 'Temps passé', 'Cartes dues demain'].map(
            (libelle, i) => (
              <div className="fc-chiffre-cle" key={libelle}>
                <dt className="fc-chiffre-cle__libelle">{libelle}</dt>
                <dd className="fc-chiffre-cle__valeur">
                  <span
                    className="fc-chargement__squelette fc-chargement__squelette--chiffre"
                    style={{ ['--largeur' as string]: '64px', ['--rang' as string]: i }}
                    aria-hidden="true"
                  />
                </dd>
              </div>
            ),
          )}
        </dl>
      </div>
      <div className="fc-ecran-statistiques__graphiques fc-chargement">
        {(['previsions', 'activite', 'retention'] as const).map((g, i) => (
          <div className={`fc-graphique-${g}`} key={g}>
            <span
              className="fc-chargement__squelette fc-chargement__squelette--titre"
              style={{ ['--largeur' as string]: '120px', ['--rang' as string]: i }}
              aria-hidden="true"
            />
            <span
              className="fc-chargement__squelette"
              style={{ height: 240, ['--rang' as string]: i + 1 }}
              aria-hidden="true"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
